import React from "react";
import SmoothScroll from 'smooth-scroll';
import {
    getElementOffsetTop,
    getNavbarHeight
} from '../helpers.jsx';
import loans from "./loans.json";

const formatAmount = amount => {
    return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const getDate = () => {
    const options = {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    };
    const now = new Date();
    return now.toLocaleDateString("en-US", options);
};

const LoanRow = ({ loan }) => {
    return (
        <tr>
            <td>
                <span className="loan-name">{loan.name}</span>
                {loan.description}
            </td>
            <td>{(loan.rate * 100).toFixed(3)}%</td>
            <td>{(loan.apr * 100).toFixed(3)}%</td>
            <td>{loan.points.toFixed(3)}</td>
            <td className="cell-right">${formatAmount(loan.closingCosts.toFixed(0))}</td>
            <td className="cell-right">
                <span className="payment">${formatAmount(loan.monthlyPayment.toFixed(0))}</span>
            </td>
        </tr>
    );
};

const LoanResults = ({ results, loanAmount, zipCode }) => {
    return (
        <div>
            <h3>Compare Mortgage Rates</h3>
            <p className="hs-mortgage__subtitle">
                Rates based on a <strong>${formatAmount(loanAmount)}</strong> loan in ZIP code{" "}
                <strong>{zipCode}</strong>
            </p>
            <div className="table-wrapper">
                <table>
                    <thead>
                        <tr>
                            <th />
                            <th>Interest rate</th>
                            <th>APR</th>
                            <th>Points</th>
                            <th className="cell-right">Closing costs</th>
                            <th className="cell-right">Monthly payment</th>
                        </tr>
                    </thead>
                    <tbody>
                        {results.map((row, index) => (
                            <LoanRow key={index} loan={row} />
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="disclaimer">
                <p>
                    Rates, terms, and fees as of {getDate()} Eastern Daylight Time and subject to change without notice.
                    Your actual rate, payment and costs could be higher. Rates shown assume borrower has excellent
                    credit (including a credit score of 740 or higher).
                </p>
                <p>
                    Select a product to view important disclosures, payments, assumptions, and APR information. Please
                    note we offer additional home loan options not displayed here.
                </p>
                <p>
                    Get an official Loan Estimate before choosing a loan. <a href="#contact-form">Contact me</a> to
                    learn more.
                </p>
            </div>
        </div>
    );
};

class MortgageCalculator extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            purchasePrice: "",
            downPayment: "",
            zipCode: "",
            loanAmount: "",
            zipCodeDisplay: "",
            results: null
        };
    }

    setField = (field, value) => {
        this.setState({
            [field]: parseInt(value, 10)
        });
    };

    getMonthlyPayment = (loanAmount, rate, years) => {
        const monthlyRate = rate / 12;
        const periods = years * 12;
        return loanAmount / (((1 - (1 / Math.pow(1 + monthlyRate, periods))) / monthlyRate) * (1 + monthlyRate));
    };

    isFormValid = () => {
        const { purchasePrice, downPayment, zipCode } = this.state;
        return purchasePrice !== "" && downPayment !== "" && zipCode !== "" && purchasePrice > downPayment;
    };

    onUpdateRates = () => {
        const { purchasePrice, downPayment, zipCode } = this.state;
        const loanAmount = purchasePrice - downPayment;
        const results = loans.map(loan => {
            const rate = loan.baseRate + (parseInt(this.state.zipCode.toString()[0], 0) / 1000);
            const apr = rate + 0.0015;
            const points = Math.random() + 0.5;
            const monthlyPayment = this.getMonthlyPayment(loanAmount, rate, loan.term);
            const closingCosts = loanAmount * rate;
            return {
                name: loan.name,
                description: loan.description,
                rate,
                apr,
                points,
                monthlyPayment,
                closingCosts
            };
        });
        this.setState({
            results,
            loanAmount,
            zipCodeDisplay: zipCode
        });
        this.scrollToResults();
    };

    scrollToResults = () => {
        const scrollToY = getElementOffsetTop(this.resultsNode) - getNavbarHeight();
        const speed = 1;

        this.scroll.animateScroll(scrollToY, null, { speed });
    };

    setResultsRef = (node) => {
        this.resultsNode = node;
    };

    componentDidMount() {
        this.scroll = new SmoothScroll();
    }

    render() {
        return (
            <div className="hs-mortgage hs-contact">
                <div className="container">
                    <div className="row">
                        <div className="col-xs-12 col-md-12">
                            <div className="page-header">
                                <h2>What would you pay?</h2>
                                <p className="hs-mortgage__subtitle">
                                    <sup>*</sup> marked field are required
                                </p>
                            </div>

                            <div className="form-group">
                                <label htmlFor="purchasePrice" className="control-label">
                                    <span>Purchase price</span>
                                    <sup>*</sup>
                                </label>
                                <input
                                    id="purchasePrice"
                                    type="number"
                                    onChange={ev => this.setField("purchasePrice", ev.target.value)}
                                    value={this.state.purchasePrice}
                                    aria-required={true}
                                    required={true}
                                    className="form-control"
                                    placeholder="$"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="downPayment" className="control-label">
                                    <span>Down payment</span>
                                    <sup>*</sup>
                                </label>
                                <input
                                    id="downPayment"
                                    type="number"
                                    onChange={ev => this.setField("downPayment", ev.target.value)}
                                    value={this.state.downPayment}
                                    aria-required={true}
                                    required={true}
                                    className="form-control"
                                    placeholder="$"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="zipCode" className="control-label">
                                    <span>Home address ZIP code</span>
                                    <sup>*</sup>
                                </label>
                                <input
                                    id="zipCode"
                                    type="text"
                                    onChange={ev => this.setField("zipCode", ev.target.value)}
                                    value={this.state.zipCode}
                                    aria-required={true}
                                    required={true}
                                    className="form-control"
                                />
                            </div>

                            <button
                                type="button"
                                className="btn btn-hs contact-btn"
                                disabled={!this.isFormValid()}
                                onClick={this.onUpdateRates}
                            >
                                Calculate rate
                            </button>

                            <div ref={this.setResultsRef}>
                                {this.state.results && (
                                    <LoanResults
                                        results={this.state.results}
                                        loanAmount={this.state.loanAmount}
                                        zipCode={this.state.zipCodeDisplay}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default MortgageCalculator;



// WEBPACK FOOTER //
// ./components/mortgage_calculator/index.jsx